import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Input } from "../../../common/u1/Input";
import { FieldLabel } from "../../../common/u1/FieldLabel";
import {
  useGetAvailableCategoryQuery,
  useCreateCorporateTaxMutation,
  useUpdateCategoryMutation,
} from "../../../../store/Company/companyApiSlice";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import CreatableSelectComponent from "../../../common/CreatableSelectComponent";
import { EditDialog } from "./EditDialog";
import { FileDropzone } from "./FileDropzone";
import {
  categoryOptions,
  editingCategoryOption,
  taxPayload,
  validateTax,
} from "./taxFields";

/* Add / edit one tax registration. */
export const TaxFormModal = ({ record, onClose, onSaved }) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const { data: available } = useGetAvailableCategoryQuery({ token, id: companyID });

  const [options, setOptions] = useState([]);
  const [category, setCategory] = useState(null);
  const [taxNumber, setTaxNumber] = useState("");
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});

  const [createCorporateTax, { isLoading: creating }] = useCreateCorporateTaxMutation();
  const [updateCategory, { isLoading: updating }] = useUpdateCategoryMutation();

  useEffect(() => {
    setOptions(categoryOptions(available));
  }, [available]);

  /* Transcribed: an existing record seeds both fields. */
  useEffect(() => {
    if (!record) return;
    setTaxNumber(record.tax_reg_no || "");
    setCategory(editingCategoryOption(record));
  }, [record]);

  const submit = async () => {
    const found = validateTax({
      taxNumber,
      category: category?.value,
      file: files[0],
      existingFile: record?.upload_file,
    });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const replacedFile = Boolean(files[0]);
    let uploadedUrl = null;

    if (replacedFile) {
      /* Transcribed call, argument for argument — note there is no `isPublic`
         here, unlike the logo and banner: a tax certificate is private. */
      const uploaded = await uploadFileToS3(
        uploadFilePath?.tax,
        files[0],
        token,
        companyID,
        "tax_document"
      );
      uploadedUrl = uploaded?.url;
    }

    const data = taxPayload({
      companyID,
      record,
      category: category?.value,
      taxNumber,
      uploadedUrl,
      replacedFile,
    });

    /* Transcribed: the live code acts on a truthy response without unwrapping,
       so an RTK error result also counts as done. Kept — unwrapping here would
       change which failures close the dialog. See FINDINGS F-057. */
    const response = record
      ? await updateCategory({ data, token })
      : await createCorporateTax({ data, token });

    if (response) onSaved?.();
  };

  return (
    <EditDialog
      /* The live dialog says "Add Tax Information" even when editing. Named
         for what it is doing. */
      title={record ? "Edit Tax Information" : "Add Tax Information"}
      onClose={onClose}
      ctaLabel="Save"
      onSubmit={submit}
      submitting={creating || updating}
    >
      <div>
        <FieldLabel as="span" variant="field">
          Category
        </FieldLabel>
        <CreatableSelectComponent
          id="u1-tax-category"
          placeholder="Select a category..."
          value={category}
          options={options}
          setOptions={setOptions}
          setSelectedValue={(value) => {
            setCategory(value);
            setErrors((prev) => ({ ...prev, category: "" }));
          }}
          isCreatable
        />
        {errors.category && (
          <p role="alert" className="mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans">
            {errors.category}
          </p>
        )}
      </div>

      <Input
        wrapperClassName="mt-3.5"
        label="Tax Number"
        id="u1-tax-number"
        value={taxNumber}
        onChange={(e) => {
          setTaxNumber(e.target.value);
          setErrors((prev) => ({ ...prev, taxNumber: "" }));
        }}
        error={errors.taxNumber}
        placeholder="Enter tax number"
      />

      <div className="mt-3.5">
        <FieldLabel as="span" variant="field">
          Supporting Document
        </FieldLabel>
        <FileDropzone
          label="Supporting Document"
          files={files}
          onChange={(next) => {
            setFiles(next);
            setErrors((prev) => ({ ...prev, file: "" }));
          }}
          error={errors.file}
        />

        {/* An edit keeps its stored document unless a new one is chosen, so it
            has to be visible that there already is one. */}
        {record?.upload_file && files.length === 0 && (
          <p className="mt-2 text-[12px] text-[var(--muted)] font-sans">
            A document is already attached.{" "}
            <a
              href={record.upload_file}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[var(--coral)] underline underline-offset-2"
            >
              View Document
            </a>
            . Choosing a file replaces it.
          </p>
        )}
      </div>
    </EditDialog>
  );
};

export default TaxFormModal;
